import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import jwtDecode from 'jwt-decode';
import '../../styles/AdminDashboard.css';

// URL base: usa variável de ambiente ou URL relativa (fallback para proxy nginx)
const API_URL = process.env.REACT_APP_API_URL || '';

interface Appointment {
  id: number;
  client_name: string;
  client_phone: string;
  service_name: string;
  date: string;
  time: string;
  status: string;
  notes: string;
}

interface ContactMessage {
  id: number;
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
  status: string;
  created_at: string;
}

interface SiteSetting {
  id: number;
  key: string;
  value: string;
  description: string;
}

interface DashboardStats {
  total_appointments: number;
  today_appointments: number;
  pending_appointments: number;
  total_clients: number;
  new_contacts: number;
}

interface Service {
  id: number;
  name: string;
  description: string;
  duration: number;
  price: number;
  is_active: boolean;
}

interface Testimonial {
  id: number;
  author_name: string;
  author_role: string;
  quote: string;
  is_active: boolean;
  display_order: number;
}

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'dashboard' | 'appointments' | 'contacts' | 'services' | 'testimonials' | 'content' | 'settings'>('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [contacts, setContacts] = useState<ContactMessage[]>([]);
  const [settings, setSettings] = useState<SiteSetting[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [testimonials, setTestimonials] = useState<Testimonial[]>([]);
  const [editingService, setEditingService] = useState<Service | null>(null);
  const [editingTestimonial, setEditingTestimonial] = useState<Testimonial | null>(null);
  const [settingsForm, setSettingsForm] = useState<Record<string, string>>({});
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');

  const getToken = () => localStorage.getItem('access_token') || '';

  useEffect(() => {
    const token = getToken();
    if (!token) {
      navigate('/admin');
      return;
    }

    try {
      const decodedToken: any = jwtDecode(token);
      if (decodedToken.exp * 1000 < Date.now()) {
        localStorage.removeItem('access_token');
        navigate('/admin');
        return;
      }
    } catch (e) {
      localStorage.removeItem('access_token');
      navigate('/admin');
      return;
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, currentPage, statusFilter]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    setSuccessMsg('');
    const token = getToken();
    const headers = { Authorization: `Bearer ${token}` };

    try {
      if (activeTab === 'dashboard') {
        const res = await axios.get(`${API_URL}/api/admin/dashboard`, { headers });
        setStats(res.data);
      } else if (activeTab === 'appointments') {
        const params: Record<string, any> = { page: currentPage, per_page: 10 };
        if (statusFilter) params.status = statusFilter;
        const res = await axios.get(`${API_URL}/api/admin/appointments`, { headers, params });
        setAppointments(res.data.appointments);
        setTotalPages(res.data.pages);
      } else if (activeTab === 'contacts') {
        const res = await axios.get(`${API_URL}/api/admin/contacts`, { headers });
        setContacts(res.data);
      } else if (activeTab === 'services') {
        const res = await axios.get(`${API_URL}/api/services`);
        setServices(res.data);
      } else if (activeTab === 'testimonials') {
        const res = await axios.get(`${API_URL}/api/testimonials`);
        setTestimonials(res.data);
      } else if (activeTab === 'content' || activeTab === 'settings') {
        const res = await axios.get(`${API_URL}/api/admin/settings`, { headers });
        setSettings(res.data);
        const form: Record<string, string> = {};
        res.data.forEach((s: SiteSetting) => { form[s.key] = s.value; });
        setSettingsForm(form);
      }
    } catch (err: any) {
      const msg = err.response?.data?.error || 'Erro ao carregar dados.';
      setError(msg);
      if (err.response?.status === 401) {
        localStorage.removeItem('access_token');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    navigate('/admin');
  };

  const handleAppointmentStatusChange = async (id: number, newStatus: string) => {
    const token = getToken();
    try {
      await axios.put(
        `${API_URL}/api/admin/appointments/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAppointments(prev =>
        prev.map(apt => apt.id === id ? { ...apt, status: newStatus } : apt)
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao atualizar status do agendamento.');
    }
  };

  const handleContactStatusChange = async (id: number, newStatus: string) => {
    const token = getToken();
    try {
      await axios.put(
        `${API_URL}/api/admin/contacts/${id}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setContacts(prev =>
        prev.map(c => c.id === id ? { ...c, status: newStatus } : c)
      );
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao atualizar status do contato.');
    }
  };

  const handleSettingsChange = (key: string, value: string) => {
    setSettingsForm(prev => ({ ...prev, [key]: value }));
  };

  const handleSettingsSave = async () => {
    const token = getToken();
    try {
      await axios.put(
        `${API_URL}/api/admin/settings`,
        settingsForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessMsg('Configurações salvas com sucesso!');
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar configurações.');
    }
  };

  const handleServiceSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingService) return;
    const token = getToken();
    try {
      if (editingService.id) {
        await axios.put(`${API_URL}/api/admin/services/${editingService.id}`, editingService, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API_URL}/api/admin/services`, editingService, { headers: { Authorization: `Bearer ${token}` } });
      }
      setSuccessMsg('Serviço salvo com sucesso!');
      setEditingService(null);
      fetchData();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar serviço.');
    }
  };

  const handleServiceDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este serviço?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API_URL}/api/admin/services/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSuccessMsg('Serviço excluído com sucesso!');
      fetchData();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao excluir serviço.');
    }
  };

  const handleTestimonialSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingTestimonial) return;
    const token = getToken();
    try {
      if (editingTestimonial.id) {
        await axios.put(`${API_URL}/api/admin/testimonials/${editingTestimonial.id}`, editingTestimonial, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post(`${API_URL}/api/admin/testimonials`, editingTestimonial, { headers: { Authorization: `Bearer ${token}` } });
      }
      setSuccessMsg('Depoimento salvo com sucesso!');
      setEditingTestimonial(null);
      fetchData();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao salvar depoimento.');
    }
  };

  const handleTestimonialDelete = async (id: number) => {
    if (!window.confirm('Tem certeza que deseja excluir este depoimento?')) return;
    const token = getToken();
    try {
      await axios.delete(`${API_URL}/api/admin/testimonials/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSuccessMsg('Depoimento excluído com sucesso!');
      fetchData();
      setTimeout(() => setSuccessMsg(''), 3000);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao excluir depoimento.');
    }
  };

  const formatDate = (dateStr: string) => {
    // Adiciona hora para evitar problema de timezone ao usar new Date()
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  const getStatusBadgeClass = (status: string) => {
    const map: Record<string, string> = {
      agendado: 'badge-warning',
      confirmado: 'badge-info',
      realizado: 'badge-success',
      cancelado: 'badge-danger',
      novo: 'badge-warning',
      lido: 'badge-info',
      respondido: 'badge-success',
    };
    return `badge ${map[status] || 'badge-secondary'}`;
  };

  const renderPagination = () => (
    <div className="pagination">
      <button
        className="pagination-button"
        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
        disabled={currentPage === 1 || loading}
      >
        ← Anterior
      </button>
      <span className="pagination-info">Página {currentPage} de {totalPages}</span>
      <button
        className="pagination-button"
        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages || loading}
      >
        Próximo →
      </button>
    </div>
  );

  const settingLabels: Record<string, string> = {
    clinic_name: 'Nome da Clínica',
    clinic_phone: 'Telefone Principal (com código do país)',
    clinic_phone2: 'Telefone Secundário',
    clinic_email: 'E-mail de Contato',
    clinic_address: 'Endereço Completo',
    clinic_hours_weekday: 'Horário – Dias Úteis',
    clinic_hours_saturday: 'Horário – Sábado',
    clinic_instagram: 'Link Instagram',
    clinic_facebook: 'Link Facebook',
    whatsapp_number: 'Número WhatsApp (com código do país)',
  };

  return (
    <div className="admin-dashboard-page">
      {/* Botão hambúrguer – visível apenas no mobile */}
      <button
        className="mobile-menu-toggle"
        onClick={() => setSidebarOpen(true)}
        aria-label="Abrir menu"
      >
        <i className="fas fa-bars"></i>
      </button>

      {/* Overlay para fechar sidebar no mobile */}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={() => setSidebarOpen(false)} />
      )}

      <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="sidebar-header">
          <div className="sidebar-logo">
            <i className="fas fa-heartbeat"></i>
          </div>
          <h3>Admin CIS</h3>
        </div>
        <nav className="sidebar-nav">
          <ul>
            <li>
              <button
                className={activeTab === 'dashboard' ? 'active' : ''}
                onClick={() => { setActiveTab('dashboard'); setCurrentPage(1); setSidebarOpen(false); }}
              >
                <i className="fas fa-tachometer-alt"></i> Dashboard
              </button>
            </li>
            <li>
              <button
                className={activeTab === 'appointments' ? 'active' : ''}
                onClick={() => { setActiveTab('appointments'); setCurrentPage(1); setSidebarOpen(false); }}
              >
                <i className="fas fa-calendar-alt"></i> Agendamentos
              </button>
            </li>
            <li>
              <button
                className={activeTab === 'contacts' ? 'active' : ''}
                onClick={() => { setActiveTab('contacts'); setCurrentPage(1); setSidebarOpen(false); }}
              >
                <i className="fas fa-envelope"></i> Mensagens
              </button>
            </li>
            <li>
              <button
                className={activeTab === 'settings' ? 'active' : ''}
                onClick={() => { setActiveTab('settings'); setSidebarOpen(false); }}
              >
                <i className="fas fa-cog"></i> Configurações
              </button>
            </li>
            <li>
              <button
                className={activeTab === 'services' ? 'active' : ''}
                onClick={() => { setActiveTab('services'); setCurrentPage(1); setSidebarOpen(false); }}
              >
                <i className="fas fa-spa"></i> Serviços
              </button>
            </li>
            <li>
              <button
                className={activeTab === 'testimonials' ? 'active' : ''}
                onClick={() => { setActiveTab('testimonials'); setCurrentPage(1); setSidebarOpen(false); }}
              >
                <i className="fas fa-comments"></i> Depoimentos
              </button>
            </li>
            <li>
              <button
                className={activeTab === 'content' ? 'active' : ''}
                onClick={() => { setActiveTab('content'); setCurrentPage(1); setSidebarOpen(false); }}
              >
                <i className="fas fa-file-alt"></i> Conteúdo
              </button>
            </li>
            <li>
              <button onClick={handleLogout} className="logout-button">
                <i className="fas fa-sign-out-alt"></i> Sair
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      <main className="dashboard-content">
        <header className="dashboard-header">
          <h1>
            {activeTab === 'dashboard' && 'Visão Geral'}
            {activeTab === 'appointments' && 'Agendamentos'}
            {activeTab === 'contacts' && 'Mensagens de Contato'}
            {activeTab === 'settings' && 'Configurações do Site'}
            {activeTab === 'services' && 'Gestão de Serviços'}
            {activeTab === 'testimonials' && 'Gestão de Depoimentos'}
            {activeTab === 'content' && 'Conteúdo das Páginas'}
          </h1>
        </header>

        {loading && <div className="loading-spinner"><div className="spinner"></div></div>}
        {error && <div className="alert alert-error"><i className="fas fa-exclamation-circle"></i> {error}</div>}
        {successMsg && <div className="alert alert-success"><i className="fas fa-check-circle"></i> {successMsg}</div>}

        {!loading && !error && (
          <div className="tab-content">

            {/* ── DASHBOARD ── */}
            {activeTab === 'dashboard' && stats && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-icon stat-icon-blue">
                    <i className="fas fa-calendar-check"></i>
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.today_appointments}</span>
                    <span className="stat-label">Agendamentos Hoje</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon stat-icon-yellow">
                    <i className="fas fa-clock"></i>
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.pending_appointments}</span>
                    <span className="stat-label">Pendentes de Confirmação</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon stat-icon-green">
                    <i className="fas fa-calendar-alt"></i>
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.total_appointments}</span>
                    <span className="stat-label">Total de Agendamentos</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon stat-icon-purple">
                    <i className="fas fa-users"></i>
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.total_clients}</span>
                    <span className="stat-label">Clientes Cadastrados</span>
                  </div>
                </div>
                <div className="stat-card">
                  <div className="stat-icon stat-icon-red">
                    <i className="fas fa-envelope"></i>
                  </div>
                  <div className="stat-info">
                    <span className="stat-number">{stats.new_contacts}</span>
                    <span className="stat-label">Novas Mensagens</span>
                  </div>
                </div>
              </div>
            )}

            {/* ── AGENDAMENTOS ── */}
            {activeTab === 'appointments' && (
              <div className="appointments-section">
                <div className="section-toolbar">
                  <label htmlFor="status-filter">Filtrar por status:</label>
                  <select
                    id="status-filter"
                    value={statusFilter}
                    onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                  >
                    <option value="">Todos</option>
                    <option value="agendado">Agendado</option>
                    <option value="confirmado">Confirmado</option>
                    <option value="realizado">Realizado</option>
                    <option value="cancelado">Cancelado</option>
                  </select>
                </div>

                {appointments.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-calendar-times"></i>
                    <p>Nenhum agendamento encontrado.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table>
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Cliente</th>
                          <th>Telefone</th>
                          <th>Serviço</th>
                          <th>Data</th>
                          <th>Hora</th>
                          <th>Status</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {appointments.map((apt) => (
                          <tr key={apt.id}>
                            <td>#{apt.id}</td>
                            <td>{apt.client_name}</td>
                            <td>
                              <a href={`https://wa.me/${apt.client_phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer">
                                {apt.client_phone}
                              </a>
                            </td>
                            <td>{apt.service_name}</td>
                            <td>{formatDate(apt.date)}</td>
                            <td>{apt.time}</td>
                            <td>
                              <span className={getStatusBadgeClass(apt.status)}>
                                {apt.status}
                              </span>
                            </td>
                            <td>
                              <select
                                value={apt.status}
                                onChange={(e) => handleAppointmentStatusChange(apt.id, e.target.value)}
                                className="status-select"
                              >
                                <option value="agendado">Agendado</option>
                                <option value="confirmado">Confirmado</option>
                                <option value="realizado">Realizado</option>
                                <option value="cancelado">Cancelado</option>
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                {renderPagination()}
              </div>
            )}

            {/* ── CONTATOS ── */}
            {activeTab === 'contacts' && (
              <div className="contacts-section">
                {contacts.length === 0 ? (
                  <div className="empty-state">
                    <i className="fas fa-inbox"></i>
                    <p>Nenhuma mensagem de contato encontrada.</p>
                  </div>
                ) : (
                  <div className="contacts-list">
                    {contacts.map((msg) => (
                      <div key={msg.id} className={`contact-card contact-card-${msg.status}`}>
                        <div className="contact-card-header">
                          <div className="contact-meta">
                            <strong>{msg.name}</strong>
                            <span className={getStatusBadgeClass(msg.status)}>{msg.status}</span>
                          </div>
                          <span className="contact-date">
                            {new Date(msg.created_at).toLocaleDateString('pt-BR', {
                              day: '2-digit', month: '2-digit', year: 'numeric',
                              hour: '2-digit', minute: '2-digit'
                            })}
                          </span>
                        </div>
                        <div className="contact-card-body">
                          {msg.subject && <p className="contact-subject"><strong>Assunto:</strong> {msg.subject}</p>}
                          <p className="contact-message">{msg.message}</p>
                          <div className="contact-info-row">
                            {msg.phone && (
                              <a href={`https://wa.me/${msg.phone.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="contact-link">
                                <i className="fab fa-whatsapp"></i> {msg.phone}
                              </a>
                            )}
                            {msg.email && (
                              <a href={`mailto:${msg.email}`} className="contact-link">
                                <i className="fas fa-envelope"></i> {msg.email}
                              </a>
                            )}
                          </div>
                        </div>
                        <div className="contact-card-footer">
                          <label>Marcar como:</label>
                          <select
                            value={msg.status}
                            onChange={(e) => handleContactStatusChange(msg.id, e.target.value)}
                            className="status-select"
                          >
                            <option value="novo">Novo</option>
                            <option value="lido">Lido</option>
                            <option value="respondido">Respondido</option>
                          </select>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* ── SERVIÇOS ── */}
            {activeTab === 'services' && (
              <div className="services-section">
                <div className="section-toolbar">
                  <button className="btn btn-primary" onClick={() => setEditingService({ id: 0, name: '', description: '', duration: 60, price: 0, is_active: true })}>
                    <i className="fas fa-plus"></i> Novo Serviço
                  </button>
                </div>

                {editingService && (
                  <form className="settings-form modal-like" onSubmit={handleServiceSave}>
                    <h3 style={{ marginBottom: '1rem' }}>{editingService.id ? 'Editar Serviço' : 'Novo Serviço'}</h3>
                    <div className="form-group">
                      <label>Nome</label>
                      <input type="text" value={editingService.name} onChange={e => setEditingService({...editingService, name: e.target.value})} required />
                    </div>
                    <div className="form-group">
                      <label>Descrição</label>
                      <textarea value={editingService.description} onChange={e => setEditingService({...editingService, description: e.target.value})} required rows={3}></textarea>
                    </div>
                    <div className="form-group-row" style={{ display: 'flex', gap: '15px' }}>
                      <div className="form-group">
                        <label>Duração (min)</label>
                        <input type="number" value={editingService.duration || ''} onChange={e => setEditingService({...editingService, duration: parseInt(e.target.value)})} />
                      </div>
                      <div className="form-group">
                        <label>Preço (R$)</label>
                        <input type="number" value={editingService.price || ''} onChange={e => setEditingService({...editingService, price: parseFloat(e.target.value)})} />
                      </div>
                    </div>
                    <div className="form-group checkbox-group" style={{ flexDirection: 'row', alignItems: 'center' }}>
                      <input type="checkbox" id="is_active_svc" checked={editingService.is_active} onChange={e => setEditingService({...editingService, is_active: e.target.checked})} style={{ width: 'auto', marginRight: '8px' }} />
                      <label htmlFor="is_active_svc" style={{ margin: 0 }}>Ativo</label>
                    </div>
                    <div className="form-actions" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                      <button type="submit" className="btn btn-primary">Salvar</button>
                      <button type="button" className="btn btn-outline" onClick={() => setEditingService(null)}>Cancelar</button>
                    </div>
                  </form>
                )}

                {!editingService && (
                  <div className="table-responsive">
                    <table>
                      <thead><tr><th>Nome</th><th>Descrição</th><th>Status</th><th>Ações</th></tr></thead>
                      <tbody>
                        {services.map(s => (
                          <tr key={s.id}>
                            <td>{s.name}</td>
                            <td>{s.description.substring(0, 50)}...</td>
                            <td><span className={s.is_active ? 'badge badge-success' : 'badge badge-secondary'}>{s.is_active ? 'Ativo' : 'Inativo'}</span></td>
                            <td style={{ display: 'flex', gap: '8px' }}>
                              <button className="btn-icon" onClick={() => setEditingService(s)} title="Editar"><i className="fas fa-edit"></i></button>
                              <button className="btn-icon text-danger" onClick={() => handleServiceDelete(s.id)} title="Excluir"><i className="fas fa-trash"></i></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── DEPOIMENTOS ── */}
            {activeTab === 'testimonials' && (
              <div className="testimonials-section">
                <div className="section-toolbar">
                  <button className="btn btn-primary" onClick={() => setEditingTestimonial({ id: 0, author_name: '', author_role: '', quote: '', is_active: true, display_order: 0 })}>
                    <i className="fas fa-plus"></i> Novo Depoimento
                  </button>
                </div>

                {editingTestimonial && (
                  <form className="settings-form modal-like" onSubmit={handleTestimonialSave}>
                    <h3 style={{ marginBottom: '1rem' }}>{editingTestimonial.id ? 'Editar Depoimento' : 'Novo Depoimento'}</h3>
                    <div className="form-group-row" style={{ display: 'flex', gap: '15px' }}>
                      <div className="form-group" style={{ flex: 1 }}>
                        <label>Autor</label>
                        <input type="text" value={editingTestimonial.author_name} onChange={e => setEditingTestimonial({...editingTestimonial, author_name: e.target.value})} required />
                      </div>
                      <div className="form-group" style={{ flex: 1 }}>
                        <label>Função/Cidade (opcional)</label>
                        <input type="text" value={editingTestimonial.author_role} onChange={e => setEditingTestimonial({...editingTestimonial, author_role: e.target.value})} />
                      </div>
                    </div>
                    <div className="form-group">
                      <label>Depoimento</label>
                      <textarea value={editingTestimonial.quote} onChange={e => setEditingTestimonial({...editingTestimonial, quote: e.target.value})} required rows={4}></textarea>
                    </div>
                    <div className="form-group-row" style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                      <div className="form-group" style={{ width: '100px' }}>
                        <label>Ordem</label>
                        <input type="number" value={editingTestimonial.display_order} onChange={e => setEditingTestimonial({...editingTestimonial, display_order: parseInt(e.target.value)})} />
                      </div>
                      <div className="form-group checkbox-group" style={{ flexDirection: 'row', alignItems: 'center', marginTop: '15px' }}>
                        <input type="checkbox" id="is_active_test" checked={editingTestimonial.is_active} onChange={e => setEditingTestimonial({...editingTestimonial, is_active: e.target.checked})} style={{ width: 'auto', marginRight: '8px' }} />
                        <label htmlFor="is_active_test" style={{ margin: 0 }}>Ativo</label>
                      </div>
                    </div>
                    <div className="form-actions" style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                      <button type="submit" className="btn btn-primary">Salvar</button>
                      <button type="button" className="btn btn-outline" onClick={() => setEditingTestimonial(null)}>Cancelar</button>
                    </div>
                  </form>
                )}

                {!editingTestimonial && (
                  <div className="table-responsive">
                    <table>
                      <thead><tr><th>Ordem</th><th>Autor</th><th>Depoimento</th><th>Ações</th></tr></thead>
                      <tbody>
                        {testimonials.map(t => (
                          <tr key={t.id}>
                            <td>{t.display_order}</td>
                            <td>{t.author_name}</td>
                            <td>{t.quote.substring(0, 50)}...</td>
                            <td style={{ display: 'flex', gap: '8px' }}>
                              <button className="btn-icon" onClick={() => setEditingTestimonial(t)} title="Editar"><i className="fas fa-edit"></i></button>
                              <button className="btn-icon text-danger" onClick={() => handleTestimonialDelete(t.id)} title="Excluir"><i className="fas fa-trash"></i></button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* ── CONTEÚDO ── */}
            {activeTab === 'content' && (
              <div className="settings-section">
                <div className="settings-form">
                  <div className="settings-group-title"><h3 style={{ borderBottom: '2px solid var(--gold)', paddingBottom: '8px', color: 'var(--charcoal)', marginBottom: '1rem' }}>Textos Principais (Home)</h3></div>
                  {settings.filter(s => s.key.startsWith('hero_') || s.key.startsWith('home_')).map((s) => (
                    <div key={s.key} className="form-group">
                      <label>{s.description || s.key}</label>
                      {s.key.includes('subtitle') ? (
                        <textarea value={settingsForm[s.key] || ''} onChange={(e) => handleSettingsChange(s.key, e.target.value)} rows={3} />
                      ) : (
                        <input type="text" value={settingsForm[s.key] || ''} onChange={(e) => handleSettingsChange(s.key, e.target.value)} />
                      )}
                    </div>
                  ))}
                  
                  <div className="settings-group-title" style={{marginTop: '2rem'}}><h3 style={{ borderBottom: '2px solid var(--gold)', paddingBottom: '8px', color: 'var(--charcoal)', marginBottom: '1rem' }}>Textos (Sobre Nós)</h3></div>
                  {settings.filter(s => s.key.startsWith('about_')).map((s) => (
                    <div key={s.key} className="form-group">
                      <label>{s.description || s.key}</label>
                      {s.key.includes('history') || s.key.includes('mission') || s.key.includes('vision') ? (
                        <textarea value={settingsForm[s.key] || ''} onChange={(e) => handleSettingsChange(s.key, e.target.value)} rows={4} />
                      ) : (
                        <input type="text" value={settingsForm[s.key] || ''} onChange={(e) => handleSettingsChange(s.key, e.target.value)} />
                      )}
                    </div>
                  ))}

                  <div className="settings-group-title" style={{marginTop: '2rem'}}><h3 style={{ borderBottom: '2px solid var(--gold)', paddingBottom: '8px', color: 'var(--charcoal)', marginBottom: '1rem' }}>Textos (Serviços)</h3></div>
                  {settings.filter(s => s.key.startsWith('services_')).map((s) => (
                    <div key={s.key} className="form-group">
                      <label>{s.description || s.key}</label>
                      <input type="text" value={settingsForm[s.key] || ''} onChange={(e) => handleSettingsChange(s.key, e.target.value)} />
                    </div>
                  ))}

                  <button className="btn btn-primary" onClick={handleSettingsSave} style={{ marginTop: '20px' }}>
                    <i className="fas fa-save"></i> Salvar Conteúdo
                  </button>
                </div>
              </div>
            )}

            {/* ── CONFIGURAÇÕES ── */}
            {activeTab === 'settings' && (
              <div className="settings-section">
                <div className="settings-form">
                  <div className="settings-group-title"><h3 style={{ borderBottom: '2px solid var(--gold)', paddingBottom: '8px', color: 'var(--charcoal)', marginBottom: '1rem' }}>Dados da Clínica</h3></div>
                  {settings.filter(s => s.key.startsWith('clinic_') || s.key.startsWith('whatsapp_')).map((s) => (
                    <div key={s.key} className="form-group">
                      <label htmlFor={`setting-${s.key}`}>
                        {settingLabels[s.key] || s.description || s.key}
                      </label>
                      <input
                        id={`setting-${s.key}`}
                        type="text"
                        value={settingsForm[s.key] || ''}
                        onChange={(e) => handleSettingsChange(s.key, e.target.value)}
                      />
                    </div>
                  ))}
                  <button className="btn btn-primary" onClick={handleSettingsSave} style={{ marginTop: '20px' }}>
                    <i className="fas fa-save"></i> Salvar Configurações
                  </button>
                </div>
              </div>
            )}

          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
